'use client'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import Image from 'next/image'

export function AboutLegacy() {
    const containerRef = useRef<HTMLDivElement>(null)

    // Track scroll progress within this container (0 to 1)
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    })

    // --- Animation Transforms ---

    // 1. Image Expansion (Width & Height)
    // 0-30%: Static
    // 30-100%: Expands to full viewport
    const width = useTransform(scrollYProgress, [0, 0.3, 1], ["40%", "40%", "100%"])
    const height = useTransform(scrollYProgress, [0, 0.3, 1], ["80vh", "80vh", "100vh"])
    const borderRadius = useTransform(scrollYProgress, [0, 0.3, 1], ["4px", "4px", "0px"])

    // 2. Text Animations
    // Phase 1 Text (Funded Year info) - Fades out as image expands
    const text1Opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0])
    const text1Y = useTransform(scrollYProgress, [0, 0.2], [0, -50])

    // Phase 2 Text (Overlay) - Fades in near end
    const text2Opacity = useTransform(scrollYProgress, [0.7, 0.9], [0, 1])
    const text2Y = useTransform(scrollYProgress, [0.7, 0.9], [50, 0])

    // "50" Background parallax
    const numberY = useTransform(scrollYProgress, [0, 1], [0, -100])

    const overlayOpacity = useTransform(scrollYProgress, [0.6, 1], [0, 0.4])

    return (
        <section ref={containerRef} className="relative h-[300vh] bg-background">
            <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">

                {/* --- Background Pattern --- */}
                <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-10" />

                {/* --- Central Image (Expands) --- */}
                <motion.div
                    style={{ width, height, borderRadius }}
                    className="relative shadow-2xl overflow-hidden z-0"
                >
                    <Image
                        src="/architectural_artistry-f6f4c12f-1135-423e-9954-c081f9385725.png"
                        alt="Legacy of Excellence"
                        fill
                        className="object-cover"
                        priority
                    />
                    {/* Overlay gradient for text readability */}
                    <motion.div
                        style={{ opacity: overlayOpacity }}
                        className="absolute inset-0 bg-black"
                    />
                </motion.div>

                {/* --- Text Content Layer --- */}
                <div className="absolute inset-0 flex items-center justify-between container mx-auto px-6 pointer-events-none">

                    {/* LEFT COLUMN: PHASE 1 TEXT */}
                    <motion.div
                        style={{ opacity: text1Opacity, y: text1Y }}
                        className="w-full md:w-1/3 text-left relative z-10"
                    >
                        {/* Big "50" Background */}
                        <motion.div
                            style={{ y: numberY }}
                            className="absolute -top-32 -left-10 text-[15rem] leading-none font-serif text-muted/20 select-none -z-10"
                        >
                            50
                        </motion.div>

                        <div className="space-y-4">
                            <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                                History
                            </span>
                            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                                Incredible <br />
                                <span className="italic text-primary/50">Legacy</span>
                            </h2>
                        </div>
                    </motion.div>

                    {/* RIGHT COLUMN: PHASE 1 TEXT (Description) */}
                    <motion.div
                        style={{ opacity: text1Opacity, y: text1Y }}
                        className="hidden md:block w-1/4 text-lg text-muted-foreground leading-relaxed text-justify"
                    >
                        <p>
                            Nova Realty was founded in 1976 by a first-generation entrepreneur.
                            The entity is a multinational, multiproduct group with developments
                            and investments in the UAE, India, and UK.
                        </p>
                    </motion.div>

                    {/* CENTER OVERLAY: PHASE 2 TEXT (Appears later) */}
                    <motion.div
                        style={{ opacity: text2Opacity, y: text2Y }}
                        className="absolute inset-0 flex items-center justify-center z-20"
                    >
                        <div className="text-center text-white p-12 max-w-4xl">
                            <h3 className="text-5xl md:text-7xl font-serif mb-8 drop-shadow-xl">
                                Building Excellence for <br />
                                <span className="italic">Generations</span>
                            </h3>
                            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
                                At Nova Realty, we pride ourselves on our uncompromising standards.
                                Our focus on craftsmanship and detailing is a part of our legacy,
                                spanning five decades.
                            </p>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    )
}
